import * as React from 'react';

const LONG_PRESS_DURATION = 400;
// How long after a touch interaction ends to keep ignoring the mouse events
// browsers synthesize from it (touchend fires mousedown/mouseup/click).
const SYNTHETIC_MOUSE_SUPPRESS_MS = 700;

export type LongPressProps = {
  onTouchStart: () => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
  onTouchMove: () => void;
  onMouseDown: (event: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onContextMenu: (event: React.MouseEvent) => void;
  style: React.CSSProperties;
};

/**
 * Long-press detection that works for both mouse and touch. Spread the
 * returned `longPressProps` onto the pressable element and give it the
 * `select-none` class. In the element's onClick, call `consumeLongPress()`
 * first: when it returns true, preventDefault and ignore the click — it is
 * the tail end of a long press, not a tap.
 *
 * The returned style disables the iOS link-preview callout; onContextMenu
 * suppresses Android's native link menu once a long press has fired.
 */
export const useLongPress = (
  onLongPress: (() => void) | undefined,
): {longPressProps: LongPressProps; consumeLongPress: () => boolean} => {
  const pressTimerRef = React.useRef<number | null>(null);
  const suppressMouseTimerRef = React.useRef<number | null>(null);
  const triggeredRef = React.useRef(false);
  const suppressMouseRef = React.useRef(false);
  const onLongPressRef = React.useRef(onLongPress);
  onLongPressRef.current = onLongPress;

  const clearPressTimer = React.useCallback(() => {
    if (pressTimerRef.current !== null) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => {
      clearPressTimer();
      if (suppressMouseTimerRef.current !== null) {
        window.clearTimeout(suppressMouseTimerRef.current);
      }
    };
  }, [clearPressTimer]);

  const startPressTimer = React.useCallback(() => {
    triggeredRef.current = false;
    if (!onLongPressRef.current) {
      return;
    }
    clearPressTimer();
    pressTimerRef.current = window.setTimeout(() => {
      pressTimerRef.current = null;
      triggeredRef.current = true;
      onLongPressRef.current?.();
    }, LONG_PRESS_DURATION);
  }, [clearPressTimer]);

  const handleTouchStart = React.useCallback(() => {
    suppressMouseRef.current = true;
    if (suppressMouseTimerRef.current !== null) {
      window.clearTimeout(suppressMouseTimerRef.current);
      suppressMouseTimerRef.current = null;
    }
    startPressTimer();
  }, [startPressTimer]);

  const handleTouchDone = React.useCallback(() => {
    clearPressTimer();
    if (suppressMouseTimerRef.current !== null) {
      window.clearTimeout(suppressMouseTimerRef.current);
    }
    suppressMouseTimerRef.current = window.setTimeout(() => {
      suppressMouseRef.current = false;
      suppressMouseTimerRef.current = null;
    }, SYNTHETIC_MOUSE_SUPPRESS_MS);
  }, [clearPressTimer]);

  const handleTouchMove = React.useCallback(() => {
    clearPressTimer();
  }, [clearPressTimer]);

  const handleMouseDown = React.useCallback(
    (event: React.MouseEvent) => {
      if (suppressMouseRef.current || event.button !== 0) {
        return;
      }
      startPressTimer();
    },
    [startPressTimer],
  );

  const handleMouseUp = React.useCallback(() => {
    if (suppressMouseRef.current) {
      return;
    }
    clearPressTimer();
  }, [clearPressTimer]);

  const handleContextMenu = React.useCallback((event: React.MouseEvent) => {
    if (triggeredRef.current) {
      event.preventDefault();
    }
  }, []);

  const consumeLongPress = React.useCallback(() => {
    const wasTriggered = triggeredRef.current;
    triggeredRef.current = false;
    return wasTriggered;
  }, []);

  const longPressProps = React.useMemo<LongPressProps>(
    () => ({
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchDone,
      onTouchCancel: handleTouchDone,
      onTouchMove: handleTouchMove,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseUp,
      onContextMenu: handleContextMenu,
      style: {WebkitTouchCallout: 'none'},
    }),
    [
      handleTouchStart,
      handleTouchDone,
      handleTouchMove,
      handleMouseDown,
      handleMouseUp,
      handleContextMenu,
    ],
  );

  return {longPressProps, consumeLongPress};
};
